import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const FractionCalculator = () => {
  const [operation, setOperation] = useState('add');
  const [numerator1, setNumerator1] = useState('');
  const [denominator1, setDenominator1] = useState('');
  const [numerator2, setNumerator2] = useState('');
  const [denominator2, setDenominator2] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleOperationChange = (e) => {
    setOperation(e.target.value);
  };

  const handleNumerator1Change = (e) => {
    setNumerator1(e.target.value);
    setError('');
  };

  const handleDenominator1Change = (e) => {
    setDenominator1(e.target.value);
    setError('');
  };

  const handleNumerator2Change = (e) => {
    setNumerator2(e.target.value);
    setError('');
  };

  const handleDenominator2Change = (e) => {
    setDenominator2(e.target.value);
    setError('');
  };

  const calculateFraction = () => {
    if (!numerator1 || !denominator1 || !numerator2 || !denominator2) {
      setError('Please enter all required values.');
      return;
    }

    const num1 = parseFloat(numerator1);
    const den1 = parseFloat(denominator1);
    const num2 = parseFloat(numerator2);
    const den2 = parseFloat(denominator2);

    if (isNaN(num1) || isNaN(den1) || isNaN(num2) || isNaN(den2) || den1 === 0 || den2 === 0) {
      setError('Please enter valid numbers and non-zero denominators.');
      return;
    }

    let resultNum, resultDen;

    switch (operation) {
      case 'add':
        resultNum = num1 * den2 + num2 * den1;
        resultDen = den1 * den2;
        break;
      case 'subtract':
        resultNum = num1 * den2 - num2 * den1;
        resultDen = den1 * den2;
        break;
      case 'multiply':
        resultNum = num1 * num2;
        resultDen = den1 * den2;
        break;
      case 'divide':
        resultNum = num1 * den2;
        resultDen = den1 * num2;
        break;
      default:
        setError('Please select a valid operation.');
        return;
    }

    const gcd = (a, b) => (b ? gcd(b, a % b) : a);
    const commonDivisor = gcd(resultNum, resultDen);
    resultNum /= commonDivisor;
    resultDen /= commonDivisor;

    setResult({ numerator: resultNum, denominator: resultDen });

    // Update chart data
    setChartData({
      labels: ['Fraction 1', 'Fraction 2', 'Result'],
      datasets: [
        {
          label: 'Fraction Calculation',
          data: [num1 / den1, num2 / den2, resultNum / resultDen],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 206, 86, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateFraction();
  };

  return (
    <>
      <Helmet>
        <title>Fraction Calculator | BioLab Tools</title>
        <meta name="description" content="Perform arithmetic operations on fractions with ease. Add, subtract, multiply, and divide fractions accurately." />
        <meta name="keywords" content="fraction calculator, add fractions, subtract fractions, multiply fractions, divide fractions" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">🔍</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Fraction Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Perform arithmetic operations on fractions with ease. Add, subtract, multiply, and divide fractions accurately.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Operation</label>
                <select
                  name="operation"
                  value={operation}
                  onChange={handleOperationChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="add">Add (+)</option>
                  <option value="subtract">Subtract (-)</option>
                  <option value="multiply">Multiply (×)</option>
                  <option value="divide">Divide (÷)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fraction 1</label>
                <div className="flex space-x-4">
                  <input
                    type="number"
                    value={numerator1}
                    onChange={handleNumerator1Change}
                    className="w-1/2 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Numerator"
                  />
                  <input
                    type="number"
                    value={denominator1}
                    onChange={handleDenominator1Change}
                    className="w-1/2 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Denominator"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fraction 2</label>
                <div className="flex space-x-4">
                  <input
                    type="number"
                    value={numerator2}
                    onChange={handleNumerator2Change}
                    className="w-1/2 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Numerator"
                  />
                  <input
                    type="number"
                    value={denominator2}
                    onChange={handleDenominator2Change}
                    className="w-1/2 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Denominator"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Calculate
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {result && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">{result.numerator}/{result.denominator}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Value' }
                            },
                            x: { title: { display: true, text: 'Fraction' } }
                          },
                          plugins: { title: { display: true, text: 'Fraction Calculation' } }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What types of operations are supported?</h3>
                <p className="text-gray-600">
                  The Fraction Calculator supports addition, subtraction, multiplication, and division of fractions.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate are the calculations?</h3>
                <p className="text-gray-600">
                  The calculations are highly accurate, using standard mathematical formulas. Results are simplified to their lowest terms.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this calculator for educational purposes?</h3>
                <p className="text-gray-600">
                  Yes, this calculator is perfect for educational purposes, helping students understand and practice fraction operations.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Fraction Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Select Operation</h3>
                <p className="text-gray-600">
                  Choose the operation you need: add, subtract, multiply, or divide.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Fractions</h3>
                <p className="text-gray-600">
                  Input the numerators and denominators for both fractions.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Calculate</h3>
                <p className="text-gray-600">
                  Click the "Calculate" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the calculation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FractionCalculator;